var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { InteractionsServiceConstants, SERVICE } from "@agentx/agentx-services";
import _ from "lodash";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { logger } from "../logger/store-logger";
import { SortOrder, TaskAssignErrorType, TasksType } from "./tasks-store.interface";
import { calculateManualAssignedTaskCount, getTaskAssignErrorType, mapContactDataToTask, mapContactToTask, mapTaskDetailsToTask } from "./tasks-store.utils";
export class TasksStore {
    constructor(userStore, openTasksStore) {
        this.privateSelectedTask = undefined;
        this.filteredSelectedTasks = observable.map();
        //Using @observable.ref as we only care if the map object ref itself has changed, individual updates are covered in AGGrid with .observe()
        this.openTasks = observable.map();
        this.queuedTasks = observable.map();
        this.scheduledTasks = observable.map();
        this.closedTasks = observable.map();
        this._isFilterChangeInProgress = false;
        this.lastRequestTimestamp = 0;
        this.lastSelectedTaskType = TasksType.Active;
        this.userStore = userStore;
        this.openTasksStore = openTasksStore;
        //https://sqbu-github.cisco.com/CBABU/wxcc-desktop/pull/4460
        //In future the reactions will be removed and replaced with the
        //approach in this PR.
        // Reactions should not update observables
        this.setupReactionForOpenTasksChanges();
        this.setupReactionForSelectedTaskChanges();
        makeObservable(this);
    }
    dispose() {
        if (this.openTasksReactionDisposer) {
            this.openTasksReactionDisposer();
            this.openTasksReactionDisposer = undefined;
        }
        if (this.selectedTaskReactionDisposer) {
            this.selectedTaskReactionDisposer();
            this.selectedTaskReactionDisposer = undefined;
        }
    }
    get selectedTask() {
        return this.privateSelectedTask;
    }
    get openTasksArray() {
        return Array.from(this.openTasks.values());
    }
    get isFilterChangeInProgress() {
        return this._isFilterChangeInProgress;
    }
    setFilterChangeInProgress(value) {
        this._isFilterChangeInProgress = value;
    }
    get manualAssignedChatTaskCount() {
        return calculateManualAssignedTaskCount(this.openTasksArray, "chat");
    }
    get manualAssignedEmailTaskCount() {
        return calculateManualAssignedTaskCount(this.openTasksArray, "email");
    }
    get manualAssignedSocialTaskCount() {
        return calculateManualAssignedTaskCount(this.openTasksArray, "social");
    }
    //Can only ever have 1 telephony task assigned, manual or otherwise
    get assignedTelephonyTaskCount() {
        return this.openTasksArray.find((task) => task.mediaType === "telephony") ? 1 : 0;
    }
    get remainingChatTaskCount() {
        return Math.max(this.userStore.channels.maChatCount - this.manualAssignedChatTaskCount, 0);
    }
    get remainingEmailTaskCount() {
        return Math.max(this.userStore.channels.maEmailCount - this.manualAssignedEmailTaskCount, 0);
    }
    get remainingSocialTaskCount() {
        return Math.max(this.userStore.channels.maSocialCount - this.manualAssignedSocialTaskCount, 0);
    }
    setSelectedTaskType(type) {
        this.lastSelectedTaskType = type;
    }
    getTasksByType(tasksType) {
        switch (tasksType) {
            case TasksType.Active:
                return this.openTasks;
            case TasksType.Queued:
                return this.queuedTasks;
            case TasksType.Scheduled:
                return this.scheduledTasks;
            case TasksType.Closed:
                return this.closedTasks;
            default:
                throw new Error("Invalid tasks type");
        }
    }
    getTaskType(taskId) {
        var _a;
        return (_a = this.getTaskAndTaskType(taskId)) === null || _a === void 0 ? void 0 : _a.type;
    }
    pollForQueuedTasks(fetchParams, forceRefresh = false) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("[TasksStore] Starting polling for queued tasks, sortBy:", fetchParams);
            this.setFilterChangeInProgress(true);
            const requestTimestamp = Date.now();
            this.lastRequestTimestamp = requestTimestamp;
            if (this.queuedPollingSubscriptionId) {
                if (_.isEqual(this.lastFetchParams, fetchParams) && !forceRefresh) {
                    this.setFilterChangeInProgress(false);
                    return;
                }
                else {
                    yield this.stopQueuedPolling(false);
                }
            }
            this.lastFetchParams = _.cloneDeep(fetchParams);
            try {
                const { data } = yield SERVICE.cms.fetchCherryPickEnabledQueues({
                    orgId: this.userStore.orgId,
                    agentId: this.userStore.agentDbId,
                    teamId: this.userStore.teamId
                });
                const queueIdsFilters = data.map((queue) => {
                    const queueFilter = {
                        filterType: InteractionsServiceConstants.FilterType.SetFilter,
                        filter: queue.id,
                        operator: InteractionsServiceConstants.FilterOperator.Equals
                    };
                    return queueFilter;
                });
                let intractionsSortParams;
                const sortOrder = (_b = (_a = this.lastFetchParams) === null || _a === void 0 ? void 0 : _a.sortBy) === null || _b === void 0 ? void 0 : _b.order;
                if (((_d = (_c = this.lastFetchParams) === null || _c === void 0 ? void 0 : _c.sortBy) === null || _d === void 0 ? void 0 : _d.field) && sortOrder) {
                    intractionsSortParams = {
                        fields: [this.lastFetchParams.sortBy.field],
                        order: sortOrder === SortOrder.Ascending
                            ? InteractionsServiceConstants.SortOrder.Ascending
                            : InteractionsServiceConstants.SortOrder.Descending
                    };
                }
                else {
                    intractionsSortParams = {
                        fields: [InteractionsServiceConstants.QueryField.CreatedTime],
                        order: InteractionsServiceConstants.SortOrder.Ascending
                    };
                }
                let filters;
                if ((_e = this.lastFetchParams) === null || _e === void 0 ? void 0 : _e.filters) {
                    filters = Object.assign({ [InteractionsServiceConstants.QueryField.QueueId]: queueIdsFilters }, this.lastFetchParams.filters);
                }
                else {
                    filters = {
                        [InteractionsServiceConstants.QueryField.QueueId]: queueIdsFilters
                    };
                }
                this.queuedPollingSubscriptionId = yield SERVICE.interactions.startQueuedPolling((taskDetails) => {
                    if (requestTimestamp >= this.lastRequestTimestamp) {
                        this.handleQueuedTasksCallback(taskDetails);
                    }
                    else {
                        logger.debug(`[TasksStore] Ignoring outdated response from previous filter (timestamp: ${requestTimestamp}, latest: ${this.lastRequestTimestamp})`);
                    }
                }, intractionsSortParams, filters);
                logger.info("[TasksStore] Successfully started queued polling with subscription ID:", this.queuedPollingSubscriptionId, filters, intractionsSortParams);
            }
            catch (error) {
                this.setFilterChangeInProgress(false);
                logger.error("[TasksStore] Error starting queued polling:", error);
                throw error;
            }
        });
    }
    fetchMoreQueuedTasks() {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("[TasksStore] Fetching next page of queued tasks:");
            if (this.queuedPollingSubscriptionId) {
                try {
                    return yield SERVICE.interactions.fetchMoreQueuedInteractions(this.queuedPollingSubscriptionId);
                }
                catch (error) {
                    logger.error("[TasksStore] Error fetching next page of queued tasks:", error);
                    throw error;
                }
            }
            return false;
        });
    }
    refreshQueuedTasks() {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("[TasksStore] Refreshing queued tasks");
            if (this.queuedPollingSubscriptionId) {
                yield this.pollForQueuedTasks(this.lastFetchParams, true);
                logger.info("[TasksStore] Successfully refreshed queued tasks polling");
            }
            else {
                logger.info("[TasksStore] No active polling subscription to refresh");
            }
        });
    }
    setSelectedTask(taskId) {
        if (taskId !== "") {
            const task = this.getTaskById(taskId);
            if (task) {
                this.privateSelectedTask = task;
            }
        }
        else {
            this.privateSelectedTask = undefined;
        }
    }
    getTaskById(taskId) {
        var _a;
        return (_a = this.getTaskAndTaskType(taskId)) === null || _a === void 0 ? void 0 : _a.task;
    }
    canAssignTask(taskId) {
        var _a;
        switch ((_a = this.getTaskById(taskId)) === null || _a === void 0 ? void 0 : _a.mediaType) {
            case "telephony":
                return this.assignedTelephonyTaskCount === 0;
            case "chat":
                return this.remainingChatTaskCount > 0;
            case "social":
                return this.remainingSocialTaskCount > 0;
            case "email":
                return this.remainingEmailTaskCount > 0;
            default:
                return false;
        }
    }
    assignTask(taskId) {
        return SERVICE.tasks
            .assignTask(taskId)
            .then((result) => {
            if (result.success) {
                logger.info("[TasksStore] Task assigned successfully:", taskId);
                this.moveTasksFromQueuedToOpen([result]);
            }
            else {
                logger.error("[TasksStore] Error assigning task:", taskId, "Error:", result.errorMessage);
            }
            return {
                success: result.success,
                errorType: getTaskAssignErrorType(result.errorCode),
                taskId
            };
        })
            .catch((error) => {
            logger.error("[TasksStore] Error assigning task:", taskId, "Error:", error);
            // Re-throw the error or return a consistent error result object
            // For consistency with TaskAssignResult
            const errorResult = {
                success: false,
                errorType: TaskAssignErrorType.UNKNOWN_ERROR,
                taskId
            };
            return errorResult;
        });
    }
    assignMultipleTasks(taskIds) {
        return SERVICE.tasks
            .assignMultipleTasks(taskIds)
            .then((result) => {
            if (result.overallStatus === "allSucceeded") {
                logger.info("[TasksStore] All tasks assigned successfully:", taskIds);
                this.moveTasksFromQueuedToOpen(result.successfulTasks);
            }
            else if (result.overallStatus === "partialFailure") {
                // Move successful tasks to open
                this.moveTasksFromQueuedToOpen(result.successfulTasks);
                logger.error("[TasksStore] Error assigning tasks:", taskIds, "Error:", result.failedTasks);
            }
            else {
                // All failed
                logger.error("[TasksStore] Error assigning tasks:", taskIds, "Error:", result.failedTasks);
            }
            return this.mapToMultipleTasksStoreAssignResult(result);
        })
            .catch((error) => {
            logger.error("[TasksStore] Unexpected error during multiple task assignment:", taskIds, "Error:", error);
            // Create failure result for all tasks
            const failedAssignments = taskIds.map((taskId) => ({
                success: false,
                errorType: TaskAssignErrorType.UNKNOWN_ERROR,
                taskId
            }));
            return {
                overallStatus: "allFailed",
                successfulTasks: [],
                failedTasks: failedAssignments
            };
        });
    }
    stopQueuedPolling(clearTasks = true) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.queuedPollingSubscriptionId) {
                try {
                    yield SERVICE.interactions.stopInteractionUpdates(this.queuedPollingSubscriptionId);
                    this.queuedPollingSubscriptionId = undefined;
                    if (clearTasks) {
                        //In the case of filter changing we don't want to clear the queued tasks
                        //until we get the result or the UI will flicker
                        this.queuedTasks.clear();
                    }
                }
                catch (error) {
                    logger.error("[TasksStore] Error stopping queued polling:", error);
                }
            }
        });
    }
    handleQueuedTasksCallback(data) {
        var _a;
        logger.debug("[TasksStore] Received queued tasks update, count:", data.length);
        const selectedTaskId = (_a = this.privateSelectedTask) === null || _a === void 0 ? void 0 : _a.id;
        const selectedTaskWillBeFiltered = selectedTaskId && !data.some((taskDetail) => taskDetail.id === selectedTaskId);
        if (this.isFilterChangeInProgress) {
            runInAction(() => {
                if (selectedTaskWillBeFiltered && selectedTaskId) {
                    const existingTask = this.queuedTasks.get(selectedTaskId);
                    if (existingTask) {
                        logger.info(`[TasksStore] Preserving filtered selected task: ${selectedTaskId}`);
                        this.filteredSelectedTasks.set(selectedTaskId, {
                            task: existingTask,
                            type: TasksType.Queued
                        });
                    }
                }
                this.queuedTasks.clear();
                data.forEach((taskDetail) => {
                    if (taskDetail.isActive && taskDetail.status === "parked") {
                        const mappedTask = mapTaskDetailsToTask(taskDetail, this.userStore.isMaskSensitiveDataOrgSettingEnabled);
                        const openTask = this.openTasks.get(mappedTask.id);
                        if (openTask === null || openTask === void 0 ? void 0 : openTask.uiProcessing) {
                            return;
                        }
                        this.queuedTasks.set(mappedTask.id, mappedTask);
                        if (this.filteredSelectedTasks.has(mappedTask.id)) {
                            this.filteredSelectedTasks.delete(mappedTask.id);
                        }
                    }
                });
                this.setFilterChangeInProgress(false);
            });
        }
        else {
            const validTasks = [];
            const tasksToRemove = [];
            data.forEach((taskDetail) => {
                if (taskDetail.isActive && taskDetail.status === "parked") {
                    const mappedTask = mapTaskDetailsToTask(taskDetail, this.userStore.isMaskSensitiveDataOrgSettingEnabled);
                    validTasks.push(mappedTask);
                }
                else if (taskDetail.id) {
                    tasksToRemove.push(taskDetail.id);
                }
            });
            tasksToRemove.forEach((taskId) => {
                if (this.queuedTasks.has(taskId)) {
                    this.queuedTasks.delete(taskId);
                    logger.debug(`[TasksStore] Removed task from queue: ${taskId}`);
                }
            });
            validTasks.forEach((task) => {
                const openTask = this.openTasks.get(task.id);
                if (openTask === null || openTask === void 0 ? void 0 : openTask.uiProcessing) {
                    return;
                }
                if (this.filteredSelectedTasks.has(task.id)) {
                    this.filteredSelectedTasks.delete(task.id);
                    logger.debug(`[TasksStore] Task now visible, removed from filtered collection: ${task.id}`);
                }
                const existingTask = this.queuedTasks.get(task.id);
                if (existingTask) {
                    Object.assign(existingTask, task);
                }
                else {
                    this.queuedTasks.set(task.id, task);
                }
            });
        }
    }
    moveTasksFromQueuedToOpen(taskAssignResults) {
        runInAction(() => {
            taskAssignResults.forEach(({ taskId, contact }) => {
                if (!contact) {
                    logger.warn(`[TasksStore] _moveTasksFromQueuedToOpen called for taskId '${taskId}' but no contact object was provided (assignment might have failed for this task).`);
                    return;
                }
                if (this.queuedTasks.has(taskId)) {
                    this.openTasks.set(taskId, mapContactDataToTask(contact.data, this.userStore.isMaskSensitiveDataOrgSettingEnabled, true));
                    this.queuedTasks.delete(taskId);
                    logger.info("[TasksStore] Moved task from queued to open:", taskId);
                }
                else {
                    if (!this.openTasks.has(taskId)) {
                        this.openTasks.set(taskId, mapContactDataToTask(contact.data, this.userStore.isMaskSensitiveDataOrgSettingEnabled, true));
                        logger.info("[TasksStore] Task not in queued, added/updated in openTasks:", taskId);
                    }
                    else {
                        const existingOpenTask = this.openTasks.get(taskId);
                        if (existingOpenTask && !existingOpenTask.uiProcessing) {
                            this.openTasks.set(taskId, mapContactDataToTask(contact.data, this.userStore.isMaskSensitiveDataOrgSettingEnabled, true));
                            logger.info("[TasksStore] Updated existing open task with full contact details:", taskId);
                        }
                        else if (!existingOpenTask) {
                            this.openTasks.set(taskId, mapContactDataToTask(contact.data, this.userStore.isMaskSensitiveDataOrgSettingEnabled, true));
                        }
                    }
                    logger.warn("[TasksStore] Attempted to move task that was not found in queuedTasks:", taskId);
                }
            });
        });
    }
    mapToMultipleTasksStoreAssignResult(serviceResult) {
        return {
            overallStatus: serviceResult.overallStatus,
            successfulTasks: serviceResult.successfulTasks.map((task) => ({
                success: task.success,
                taskId: task.taskId
            })),
            failedTasks: serviceResult.failedTasks.map((task) => ({
                success: task.success,
                taskId: task.taskId,
                trackingId: task.trackingId
            }))
        };
    }
    findRemovedTasks(newTasks) {
        const newMap = new Map(newTasks.map((task) => [task.id, task]));
        const removed = [];
        for (const [id, task] of this.queuedTasks.entries()) {
            if (!newMap.has(id)) {
                removed.push(task);
            }
        }
        return removed;
    }
    fetchHistoryByAgent(agentId, isMaskSensitiveDataEnabled) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield SERVICE.tahoe.fetchAgentHistory({ agentId });
            logger.info(`[fetchHistoryByAgent: TahoeAPI]
      => Total contact history by agent found [${resp.contacts ? resp.contacts.length : 0}]`);
            runInAction(() => {
                resp.contacts.forEach((contact) => {
                    const task = mapContactToTask(contact, isMaskSensitiveDataEnabled);
                    this.closedTasks.set(task.sid, task);
                });
            });
        });
    }
    setupReactionForOpenTasksChanges() {
        // Need to determine if this is the best way to do this, it might be better to just forward the openTasksStore taskMap directly.
        this.openTasksReactionDisposer = reaction(() => Array.from(this.openTasksStore.taskMap.entries()), (currentTasks, oldTasks) => {
            const removed = oldTasks.filter(([oldTaskId]) => !currentTasks.some(([newTaskId]) => newTaskId === oldTaskId));
            currentTasks.forEach(([taskId, task]) => {
                if (taskId) {
                    this.openTasks.set(taskId, mapContactDataToTask(task, this.userStore.isMaskSensitiveDataOrgSettingEnabled, false));
                }
            });
            removed.forEach(([taskId]) => {
                this.openTasks.delete(taskId);
            });
        });
    }
    setupReactionForSelectedTaskChanges() {
        this.selectedTaskReactionDisposer = reaction(() => this.openTasksStore.selectedTaskId, (selectedTaskId) => {
            if (selectedTaskId) {
                //When the open task selected task id changes just clear the tasks store
                //id as this handled separately for now
                this.setSelectedTask(selectedTaskId);
            }
        });
    }
    getTaskAndTaskType(taskId) {
        let task = this.openTasks.get(taskId);
        if (task) {
            return { task, type: TasksType.Active };
        }
        task = this.queuedTasks.get(taskId);
        if (task) {
            return { task, type: TasksType.Queued };
        }
        task = this.scheduledTasks.get(taskId);
        if (task) {
            return { task, type: TasksType.Scheduled };
        }
        task = this.closedTasks.get(taskId);
        if (task) {
            return { task, type: TasksType.Closed };
        }
        const filteredTask = this.filteredSelectedTasks.get(taskId);
        if (filteredTask) {
            return filteredTask;
        }
        return undefined;
    }
    restoreSelectedTask(taskId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!taskId) {
                    logger.error("[TasksStore] Cannot restore task: taskId is empty");
                    return false;
                }
                logger.info(`[TasksStore] Attempting to restore task: ${taskId}`);
                const existingTask = this.getTaskById(taskId);
                if (existingTask) {
                    logger.info(`[TasksStore] Task found in current state: ${taskId}`);
                    this.privateSelectedTask = existingTask;
                    return true;
                }
                logger.info(`[TasksStore] Task not found locally, fetching from server: ${taskId}`);
                try {
                    const fetchedTask = yield SERVICE.interactions.fetchQueuedTaskById(taskId);
                    if (fetchedTask) {
                        const mappedTask = mapTaskDetailsToTask(fetchedTask, this.userStore.isMaskSensitiveDataOrgSettingEnabled);
                        runInAction(() => {
                            this.queuedTasks.set(taskId, mappedTask);
                            this.privateSelectedTask = mappedTask;
                        });
                        logger.info(`[TasksStore] Successfully fetched and restored task: ${taskId}`);
                        return true;
                    }
                    else {
                        logger.warn(`[TasksStore] Task not found on server: ${taskId}`);
                        return false;
                    }
                }
                catch (error) {
                    logger.error(`[TasksStore] Error fetching task by ID: ${taskId}`, error);
                    return false;
                }
            }
            catch (error) {
                logger.error(`[TasksStore] Error in restoreSelectedTask:`, error);
                return false;
            }
        });
    }
}
__decorate([
    observable
], TasksStore.prototype, "privateSelectedTask", void 0);
__decorate([
    computed
], TasksStore.prototype, "selectedTask", null);
__decorate([
    observable
], TasksStore.prototype, "filteredSelectedTasks", void 0);
__decorate([
    observable.ref
], TasksStore.prototype, "openTasks", void 0);
__decorate([
    observable.ref
], TasksStore.prototype, "queuedTasks", void 0);
__decorate([
    observable.ref
], TasksStore.prototype, "scheduledTasks", void 0);
__decorate([
    observable.ref
], TasksStore.prototype, "closedTasks", void 0);
__decorate([
    observable
], TasksStore.prototype, "_isFilterChangeInProgress", void 0);
__decorate([
    computed
], TasksStore.prototype, "isFilterChangeInProgress", null);
__decorate([
    action
], TasksStore.prototype, "setFilterChangeInProgress", null);
__decorate([
    computed
], TasksStore.prototype, "manualAssignedChatTaskCount", null);
__decorate([
    computed
], TasksStore.prototype, "manualAssignedEmailTaskCount", null);
__decorate([
    computed
], TasksStore.prototype, "manualAssignedSocialTaskCount", null);
__decorate([
    computed
], TasksStore.prototype, "assignedTelephonyTaskCount", null);
__decorate([
    computed
], TasksStore.prototype, "remainingChatTaskCount", null);
__decorate([
    computed
], TasksStore.prototype, "remainingEmailTaskCount", null);
__decorate([
    computed
], TasksStore.prototype, "remainingSocialTaskCount", null);
__decorate([
    observable
], TasksStore.prototype, "lastSelectedTaskType", void 0);
__decorate([
    action
], TasksStore.prototype, "setSelectedTaskType", null);
__decorate([
    action
], TasksStore.prototype, "pollForQueuedTasks", null);
__decorate([
    action
], TasksStore.prototype, "fetchMoreQueuedTasks", null);
__decorate([
    action
], TasksStore.prototype, "refreshQueuedTasks", null);
__decorate([
    action
], TasksStore.prototype, "setSelectedTask", null);
__decorate([
    action
], TasksStore.prototype, "assignTask", null);
__decorate([
    action
], TasksStore.prototype, "assignMultipleTasks", null);
__decorate([
    action
], TasksStore.prototype, "stopQueuedPolling", null);
__decorate([
    action
], TasksStore.prototype, "handleQueuedTasksCallback", null);
__decorate([
    action
], TasksStore.prototype, "fetchHistoryByAgent", null);
__decorate([
    action
], TasksStore.prototype, "restoreSelectedTask", null);
